import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const MAX_SIZE = 5 * 1024 * 1024; // 5MB

const Attachment = React.forwardRef(({ getAttachmentId, handleSubmitButtonDisabled }, ref) => {
  const [uploading, setUploading] = useState(false);
  const [severity, setSeverity] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [uploadComplete, setUploadComplete] = useState(false);
  const [lastFiles, setLastFiles] = useState([]);

  const showAlert = (sev, msg) => {
    setSeverity(sev);
    setAlertMessage(msg);
    setUploadComplete(true);
  };

  const truncateFilename = (filename, maxLength = 30) => {
    if (filename.length <= maxLength) return filename;
    
    const extension = filename.slice(filename.lastIndexOf('.'));
    const nameWithoutExt = filename.slice(0, filename.lastIndexOf('.'));
    const charsToShow = maxLength - extension.length - 3; // 3 for "..."
    
    if (charsToShow < 5) return filename.slice(0, maxLength - 3) + '...';
    
    return nameWithoutExt.slice(0, charsToShow) + '...' + extension;
  };

  const uploadFiles = async (files) => {
    try {
      setUploading(true);
      handleSubmitButtonDisabled?.(true);

      const formData = new FormData();
      files.forEach((file, i) => formData.append(`File ${i + 1}`, file));

      const res = await axios.post(process.env.REACT_APP_OMS_ATTACHMENTS_URL, formData, {
        params: {
          space_id: process.env.REACT_APP_SPACE_ID,
          virusscan: true,
        },
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`,
          'Content-Type': 'multipart/form-data',
          'Cache-Control': 'No-Store',
          Accept: 'application/json',
        },
      });

      const ids = Array.isArray(res?.data?.attachment)
        ? res.data.attachment.map(a => a.id)
        : [];

      if (!ids.length) {
        showAlert('error', 'Upload failed. No attachment IDs returned.');
        return;
      }

      getAttachmentId?.(ids);
      setLastFiles(files.map(f => f.name));
      showAlert('success', 'Upload complete');
    } catch (error) {
        console.log("attachment error", error);
      const infected =
        error?.response?.data?.virusscan?.[0]?.virusscan_status === 'INFECTED';
      if (infected) {
        showAlert('error', 'This file contains a virus. Please select another file.');
      } else {
        showAlert('error', 'The file did not upload properly. Please try again.');
      }
    } finally {
      setUploading(false);
      handleSubmitButtonDisabled?.(false);
    }
  };

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections?.length) {
      const msg = fileRejections
        .map(r => {
          const name = r.file?.name || 'file';
          const reasons = (r.errors || []).map(e => e.message).join(', ');
          return `${name}: ${reasons}`;
        })
        .join(' | ');
      showAlert('error', `Some files were rejected: ${msg}`);
    }

    const valid = acceptedFiles.filter(f => f.size <= MAX_SIZE);
    const tooLarge = acceptedFiles.filter(f => f.size > MAX_SIZE);

    if (tooLarge.length) {
      showAlert('error', `File too large: ${tooLarge.map(f => f.name).join(', ')}`);
    }

    if (valid.length) {
      uploadFiles(valid);
    }
  }, []);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop,
    multiple: true,
    noClick: true, // use the button to open dialog
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif'],
      'application/pdf': ['.pdf'],
    },
    maxSize: MAX_SIZE,
  });

  React.useImperativeHandle(ref, () => ({
    handleUploadComplete: (value) => {
      setUploadComplete(value);
      if (!value) {
        setLastFiles([]);
        setSeverity('');
        setAlertMessage('');
      }
    },
    openDialog: () => openFileDialog(),
  }));

  return (
    <Box>
      <Paper
        variant="outlined"
        {...getRootProps()}
        sx={{
          p: 2,
          borderStyle: 'dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
        }}
      >
        <input {...getInputProps()} />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <CloudUploadIcon color="action" sx={{ fontSize: 28, flexShrink: 0 }} />
            <Box sx={{ minHeight: 42, display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden', flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {isDragActive ? 'Drop files here…' : 'Drag and drop files or click to upload'}
              </Typography>
              {lastFiles.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  {lastFiles.map((file, index) => (
                    <Typography 
                      key={index}
                      variant="caption" 
                      color="success.main" 
                      sx={{ 
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={file}
                    >
                      ✓ {truncateFilename(file)}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Stack>
          <Button
            onClick={openFileDialog}
            variant="contained"
            color="primary"
            size="medium"
            startIcon={!uploading && <CloudUploadIcon />}
            disabled={uploading}
            sx={{ 
              minWidth: 140,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {uploading ? <CircularProgress size={20} color="inherit" /> : 'Choose Files'}
          </Button>
        </Stack>
      </Paper>

      {uploadComplete && (
        <Box sx={{ mt: 1.5 }}>
          <Alert severity={severity} sx={{ py: 0.5 }}>{alertMessage}</Alert>
        </Box>
      )}
    </Box>
  );
});

Attachment.displayName = 'Attachment';
export default Attachment;